import { Box, BoxProps } from '@lifeomic/chroma-react/components/Box';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { TableModuleActions } from '@lifeomic/chroma-react/components/TableModule/';
import { useStyles as useTableModuleStyles } from '@lifeomic/chroma-react/components/TableModule/TableModule';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useSwitchToTemplatingItem } from '../../../contexts/templatingItemSwitch';
import { useSelectedTemplatingItem } from '../../../hooks/useSelectedTemplatingItem';
import { CompositeReportDefinitionSummary, ModelUtils } from '../../../models';
import { CopyTemplatingItemButton } from '../../Buttons/CopyTemplatingItemButton';
import { DeleteTemplatingItemButton } from '../../Buttons/DeleteTemplatingItemButton';
import { EditTemplatingItemMetadataButton } from '../../Buttons/EditTemplatingItemMetadataButton';
import { CompositeReportDefinitionIcon } from '../../Icons/CompositeReportDefinitionIcon';
import { useCommonStyles } from '../../styles';

const useStyles = makeStyles<{
  level: number;
  isSelected: boolean;
}>(theme => ({
  container: {
    border: 'none',
  },
  compositeButton: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    borderRadius: 4,
    marginLeft: props =>
      theme.spacing(props.level > 0 ? props.level * 3 + 1 : 1),
    marginRight: theme.spacing(1),
    backgroundColor: props =>
      props.isSelected ? theme.palette.action.selected : 'transparent',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  compositeName: {
    overflow: 'hidden',
    flexGrow: 1,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  tableModuleActions: {
    minWidth: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    left: 'unset!important',
    margin: theme.spacing(0.5),
  },
  icon: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
}));

type CompositeReportDefinitionNodeProps = BoxProps & {
  compositeReportDefinition: CompositeReportDefinitionSummary;
  compositeReportDefinitionNodeActions?: (
    data: CompositeReportDefinitionSummary,
  ) => React.ReactNode;
};

export const CompositeReportDefinitionNode: React.FC<
  CompositeReportDefinitionNodeProps
> = ({
  compositeReportDefinition,
  compositeReportDefinitionNodeActions,
  className,
  ...props
}) => {
  const { isSelected } = useSelectedTemplatingItem();
  const styles = useStyles({
    level: ModelUtils.getLevel(compositeReportDefinition),
    isSelected: isSelected(compositeReportDefinition),
  });
  const commonStyles = useCommonStyles();
  const tableModuleStyles = useTableModuleStyles();
  const { switchToTemplatingItem } = useSwitchToTemplatingItem();

  const handleClick = () => switchToTemplatingItem(compositeReportDefinition);

  const actions = useMemo(() => {
    return compositeReportDefinitionNodeActions?.(compositeReportDefinition);
  }, [compositeReportDefinitionNodeActions, compositeReportDefinition]);

  const definitionName = ModelUtils.getName(compositeReportDefinition);

  return (
    <Box
      className={clsx(
        tableModuleStyles.tableDataRow,
        commonStyles.fileSystemNode,
        styles.container,
        className,
      )}
      {...props}
      fullWidth
    >
      <Tooltip title={definitionName} placement="left">
        <ButtonUnstyled
          className={styles.compositeButton}
          onClick={handleClick}
        >
          <CompositeReportDefinitionIcon />
          <Text size="table" className={styles.compositeName}>
            {definitionName}
          </Text>
          <TableModuleActions
            className={clsx(
              tableModuleStyles.tableModuleActions,
              styles.tableModuleActions,
            )}
          >
            {actions ? (
              actions
            ) : (
              <>
                <CopyTemplatingItemButton
                  templatingItem={compositeReportDefinition}
                />
                <EditTemplatingItemMetadataButton
                  templatingItem={compositeReportDefinition}
                />
                <DeleteTemplatingItemButton
                  templatingItem={compositeReportDefinition}
                />
              </>
            )}
          </TableModuleActions>
        </ButtonUnstyled>
      </Tooltip>
    </Box>
  );
};
